import { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { editAsset, UpdateAssetPayload } from 'store/assetsSlice';
import { getCategories, Category, createCategory } from 'api/assetsAPI';
import { getCompanies, Company } from 'api/companyAPI';
import { getEffectiveCompanyContext } from 'api/contextAPI';

// ✅ MATCHES AssetTable: Use the same DisplayAsset type
interface DisplayAsset {
  id: number;
  name: string;
  asset_tag: string;
  serial_number: string;
  brand: string;
  model: string;
  description: string;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  condition: 'excellent' | 'good' | 'fair' | 'poor';
  purchase_date: string;
  purchase_cost: number;
  purchase_invoice_number: string;
  vendor: string;
  warranty_expiry_date: string;
  location: string;
  company_id: number;
  category_id: number;
  current_holder_id: number;
  created_at: string;
  updated_at: string;
  category_name?: string;
  current_holder_name?: string;
  company_name?: string;

  // Display fields from AssetTable
  deviceName: string;
  serialNumber: string;
  category: string;
  assignedTo?: string;
}

interface AxiosErrorType {
  response?: {
    data?: {
      detail?: string;
      message?: string;
      error?: string;
    };
  };
  message?: string;
}

interface EditAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: DisplayAsset | null;
  onSave: (updatedAsset: DisplayAsset) => void;
}

const EditAssetModal = ({
  show,
  handleClose,
  asset,
  onSave
}: EditAssetModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading: reduxLoading, error } = useSelector(
    (state: RootState) => state.assets
  );
  const { companyId, role } = useSelector((state: RootState) => state.auth);

  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // States for Super Admin dropdowns
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // State for effective company ID from context API
  const [effectiveCompanyId, setEffectiveCompanyId] = useState<number | null>(
    null
  );

  const isSuperAdmin = role === 'super_user';

  // ✅ MATCHES Backend API: Use exact backend field names (purchase_cost removed)
  const [formData, setFormData] = useState<
    Omit<
      DisplayAsset,
      | 'created_at'
      | 'updated_at'
      | 'category_name'
      | 'current_holder_name'
      | 'company_name'
      | 'deviceName'
      | 'serialNumber'
      | 'category'
      | 'assignedTo'
      | 'purchase_cost'
    >
  >({
    id: 0,
    name: '',
    asset_tag: '',
    serial_number: '',
    brand: '',
    model: '',
    description: '',
    status: 'available',
    condition: 'excellent',
    purchase_date: '',
    purchase_invoice_number: '',
    vendor: '',
    warranty_expiry_date: '',
    location: '',
    company_id: 0,
    category_id: 0,
    current_holder_id: 0
  });

  const userCompanyId = companyId ? parseInt(companyId) : 0;

  // NEW: Single loading state for all company-related data
  const [companyDataLoading, setCompanyDataLoading] = useState(false);

  // Fetch effective company from context API
  useEffect(() => {
    const fetchEffectiveCompany = async () => {
      try {
        const context = await getEffectiveCompanyContext();
        setEffectiveCompanyId(context.company_id || null);
      } catch (error) {
        console.error('Failed to fetch effective company:', error);
        setEffectiveCompanyId(userCompanyId);
      }
    };

    if (show) {
      fetchEffectiveCompany();
      // Load companies if Super Admin
      if (isSuperAdmin) {
        loadCompanies();
      }
    }
  }, [show]);

  // Update form data when asset changes
  useEffect(() => {
    if (asset) {
      setFormData({
        id: asset.id,
        name: asset.name,
        asset_tag: asset.asset_tag,
        serial_number: asset.serial_number,
        brand: asset.brand,
        model: asset.model,
        description: asset.description,
        status: asset.status,
        condition: asset.condition,
        purchase_date: asset.purchase_date,
        purchase_invoice_number: asset.purchase_invoice_number,
        vendor: asset.vendor,
        warranty_expiry_date: asset.warranty_expiry_date,
        location: asset.location,
        company_id: asset.company_id,
        category_id: asset.category_id,
        current_holder_id: asset.current_holder_id
      });
      setFormError('');

      // ✅ FIXED: Load all company data together (categories)
      if (asset.company_id > 0) {
        loadCompanyData(asset.company_id);
      }
    }
  }, [asset, isSuperAdmin]);

  // Load companies for Super Admin
  const loadCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const companiesData = await getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load companies:', error);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // ✅ FIXED: Unified function to load all company-related data in parallel
  const loadCompanyData = async (companyId: number) => {
    if (!companyId || companyId === 0) {
      setCategories([]);
      return;
    }

    setCompanyDataLoading(true);
    setCategoriesLoading(true);

    try {
      const categoriesData = await getCategories(companyId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load company data:', error);
      setCategories([]);
      setFormError('Failed to load company data. Please try again.');
    } finally {
      setCompanyDataLoading(false);
      setCategoriesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;

    setFormError('');

    // ✅ MATCHES Backend API: Validate required fields
    if (!formData.name.trim() || !formData.serial_number.trim()) {
      setFormError('Device Name and Serial Number are required');
      return;
    }

    try {
      // ✅ MATCHES Backend API: Create proper UpdateAssetPayload
      const updateData: UpdateAssetPayload = {
        assetId: asset.id,
        assetData: {
          name: formData.name,
          asset_tag: formData.asset_tag,
          serial_number: formData.serial_number,
          brand: formData.brand,
          model: formData.model,
          description: formData.description,
          status: formData.status,
          condition: formData.condition,
          purchase_date: formData.purchase_date,
          purchase_cost: asset.purchase_cost, // Keep existing purchase_cost from asset
          purchase_invoice_number: formData.purchase_invoice_number,
          vendor: formData.vendor,
          warranty_expiry_date: formData.warranty_expiry_date,
          location: formData.location,
          current_holder_id: formData.current_holder_id
        }
      };

      await dispatch(editAsset(updateData)).unwrap();

      // ✅ MATCHES AssetTable: Return updated asset with display fields
      const updatedAsset: DisplayAsset = {
        ...formData,
        purchase_cost: asset.purchase_cost, // Keep existing purchase_cost
        created_at: asset.created_at,
        updated_at: new Date().toISOString(),
        category_name: asset.category_name,
        current_holder_name: asset.current_holder_name,
        company_name: asset.company_name,
        deviceName: formData.name,
        serialNumber: formData.serial_number,
        category:
          categories.find(c => c.id === formData.category_id)?.name ||
          asset.category,
        assignedTo: asset.assignedTo
      };

      onSave(updatedAsset);
      handleClose();
    } catch (error) {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string;
            message?: string;
            error?: string;
          };
        };
        message?: string;
      };

      let errorMessage = 'Failed to update asset';

      // Extract backend error message
      if (axiosError.response?.data?.detail) {
        errorMessage = axiosError.response.data.detail;
      } else if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (axiosError.response?.data?.error) {
        errorMessage = axiosError.response.data.error;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }

      setFormError(errorMessage);
    }
  };

  // ✅ FIXED: Handle company change - load all data together
  const handleCompanyChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const companyId = Number(e.target.value);

    // Update form data immediately
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      category_id: 0,
      current_holder_id: 0
    }));

    // Load all company-related data together (no race conditions)
    await loadCompanyData(companyId);
  };

  // Create category handler
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setFormError('Category name is required');
      return;
    }

    const existingCategory = categories.find(
      cat => cat.name.toLowerCase() === newCategoryName.trim().toLowerCase()
    );

    if (existingCategory) {
      setFormError(`Category "${newCategoryName}" already exists`);
      return;
    }

    setCreatingCategory(true);
    try {
      const selectedCompanyId = isSuperAdmin
        ? formData.company_id
        : effectiveCompanyId || userCompanyId;
      const newCategory = await createCategory({
        name: newCategoryName.trim(),
        description: '',
        company_id: selectedCompanyId || 0
      });

      // ✅ FIXED: Reload all company data to get updated categories
      if (selectedCompanyId && selectedCompanyId > 0) {
        await loadCompanyData(selectedCompanyId);
      }

      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setShowCategoryModal(false);
      setFormError('');
    } catch (error) {
      console.error('Failed to create category:', error);
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string;
            message?: string;
            error?: string;
          };
        };
        message?: string;
      };

      let errorMessage = 'Failed to create category';

      if (axiosError.response?.data?.detail) {
        errorMessage = axiosError.response.data.detail;
      } else if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (axiosError.response?.data?.error) {
        errorMessage = axiosError.response.data.error;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }

      setFormError(errorMessage);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    let processedValue: string | number = value;

    if (name === 'category_id') {
      processedValue = Number(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (formError) setFormError('');
  };

  const openCategoryModal = () => {
    setNewCategoryName('');
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setNewCategoryName('');
  };

  const handleCloseModal = () => {
    setFormError('');
    handleClose();
  };

  if (!asset) return null;

  return (
    <>
      <Modal
        show={show}
        onHide={handleCloseModal}
        size="lg"
        backdrop="static"
        keyboard={true}
        style={{ zIndex: 1050 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Asset</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body
            style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
          >
            {/* Error Alert */}
            {(formError || error) && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {isSuperAdmin && (
              <Alert variant="info" className="mb-3">
                <small>
                  <strong>Super Admin Mode:</strong> You can edit assets across
                  companies.
                </small>
              </Alert>
            )}

            <Row className="g-3">
              {/* Company Selector - Only for Super Admin */}
              {isSuperAdmin && (
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Company *</Form.Label>
                    <Form.Select
                      name="company_id"
                      value={formData.company_id}
                      onChange={handleCompanyChange}
                      required
                      disabled={companyDataLoading}
                    >
                      <option value={0}>Select Company *</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Form.Select>
                    {companyDataLoading && formData.company_id > 0 && (
                      <Form.Text className="text-muted">
                        Loading company data...
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              )}

              {/* ✅ MATCHES AssetTable: All fields use backend field names */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Device Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter device name"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Asset Tag</Form.Label>
                  <Form.Control
                    type="text"
                    name="asset_tag"
                    value={formData.asset_tag}
                    onChange={handleInputChange}
                    placeholder="Enter asset tag"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Serial Number *</Form.Label>
                  <Form.Control
                    type="text"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter serial number"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Brand</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    placeholder="Enter brand"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="Enter model"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category *</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      disabled={
                        companyDataLoading ||
                        (isSuperAdmin && formData.company_id === 0)
                      }
                      style={{ flex: 1 }}
                      required
                    >
                      <option value={0}>
                        {companyDataLoading
                          ? 'Loading categories...'
                          : isSuperAdmin && formData.company_id === 0
                          ? 'Select company first'
                          : 'Select Category *'}
                      </option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={openCategoryModal}
                      disabled={
                        companyDataLoading ||
                        (isSuperAdmin && formData.company_id === 0)
                      }
                      style={{ whiteSpace: 'nowrap' }}
                      title={
                        companyDataLoading
                          ? 'Loading...'
                          : isSuperAdmin && formData.company_id === 0
                          ? 'Select a company first'
                          : 'Add new category'
                      }
                    >
                      + New
                    </Button>
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Condition</Form.Label>
                  <Form.Select
                    name="condition"
                    value={formData.condition}
                    onChange={handleInputChange}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Enter location"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Purchase Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="purchase_date"
                    value={formData.purchase_date}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Warranty Expiry Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="warranty_expiry_date"
                    value={formData.warranty_expiry_date}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Vendor</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleInputChange}
                    placeholder="Enter vendor"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Purchase Invoice Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="purchase_invoice_number"
                    value={formData.purchase_invoice_number}
                    onChange={handleInputChange}
                    placeholder="Enter invoice number"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Enter asset description"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={reduxLoading || companyDataLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                reduxLoading ||
                companyDataLoading ||
                (isSuperAdmin && formData.company_id === 0)
              }
              title={
                companyDataLoading
                  ? 'Loading company data...'
                  : isSuperAdmin && formData.company_id === 0
                  ? 'Select a company first'
                  : undefined
              }
            >
              {reduxLoading || companyDataLoading
                ? 'Loading...'
                : 'Update Asset'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Create Category Modal */}
      <Modal show={showCategoryModal} onHide={closeCategoryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Category Name *</Form.Label>
            <Form.Control
              type="text"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              placeholder="Enter category name"
              required
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateCategory();
                }
              }}
            />
            <Form.Text className="text-muted">
              This category will be saved in the database and available for all
              assets.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCategoryModal}
            disabled={creatingCategory}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateCategory}
            disabled={creatingCategory || !newCategoryName.trim()}
          >
            {creatingCategory ? 'Creating...' : 'Create Category'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditAssetModal;
